import type { VoidComponent } from "solid-js";
import { createEffect, createSignal } from "solid-js";
import pMinDelay from "p-min-delay";
import { isNil } from "lodash-es";
import DetailModal from "../../../../../components/modal/DetailModal";
import { useTranslation } from "../../../../../state/i18n";
import { useCurrentModal, useModalManager } from "../../../../../components/modal/ModalManager";
import { TextButton } from "../../../../../components/button/Button";
import { formatDate } from "../../../../../components/formatting/FormatDate";
import type { PlanInterval } from "../../../../../services/BillingService";
import BillingService from "../../../../../services/BillingService";
import { MINIMUM_ACTION_LOADING_DELAY } from "../../../../../constants";
import GenericFailureModal from "../../../../GenericFailureModal";
import CancellationInfoItem from "./CancellationInfoItem";

type UndoCancellationModalProps = {
  subscriptionId: string;
  planName: string;
  interval: PlanInterval;
  nextInvoiceDate?: Date;
};

const UndoCancellationModal: VoidComponent<UndoCancellationModalProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const { close, changeCloseBehaviour } = useCurrentModal();
  const uncancelSubscription = BillingService.uncancelSubscription();
  const [isUndoingCancellation, setIsUndoingCancellation] = createSignal(false);

  createEffect(() => {
    changeCloseBehaviour(isUndoingCancellation() ? "off" : "default");
  });

  const reactivatedDescription = (): string => {
    const options = {
      planName: props.planName,
      interval: t(($) =>
        props.interval === "month"
          ? $.SETTINGS_BILLING_CURRENT_PLAN_INTERVAL_MONTH
          : $.SETTINGS_BILLING_CURRENT_PLAN_INTERVAL_YEAR,
      ),
    };
    return isNil(props.nextInvoiceDate)
      ? t(($) => $.UNDO_CANCEL_MODAL_REACTIVATED_DESCRIPTION_WITHOUT_DATE, options)
      : t(($) => $.UNDO_CANCEL_MODAL_REACTIVATED_DESCRIPTION, {
          ...options,
          nextInvoiceDate: formatDate(props.nextInvoiceDate, true),
        });
  };

  const undoCancellation = async (): Promise<void> => {
    setIsUndoingCancellation(true);
    await pMinDelay(uncancelSubscription.mutateAsync(props.subscriptionId), MINIMUM_ACTION_LOADING_DELAY)
      .then(() => close())
      .catch((error: unknown) => open({ content: () => <GenericFailureModal error={error} /> }))
      .finally(() => setIsUndoingCancellation(false));
  };

  return (
    <DetailModal
      title={t(($) => $.UNDO_CANCEL_MODAL_TITLE)}
      description={t(($) => $.UNDO_CANCEL_MODAL_DESCRIPTION)}
      content={() => (
        <div class="flex flex-col gap-4">
          <CancellationInfoItem
            icon="checkCircle"
            title={t(($) => $.UNDO_CANCEL_MODAL_REACTIVATED_TITLE)}
            description={reactivatedDescription()}
          />
          <CancellationInfoItem
            icon="lockOpen"
            title={t(($) => $.UNDO_CANCEL_MODAL_FULL_ACCESS_TITLE)}
            description={t(($) => $.UNDO_CANCEL_MODAL_FULL_ACCESS_DESCRIPTION)}
          />
        </div>
      )}
      footer={
        <div class="flex w-full justify-end gap-2">
          <TextButton
            text={t(($) => $.UNDO_CANCEL_MODAL_KEEP_CANCELLED_TITLE)}
            variant="secondary"
            disabled={isUndoingCancellation()}
            onClick={() => close()}
          />
          <TextButton
            text={t(($) => $.UNDO_CANCEL_MODAL_UNDO_CANCELLATION_TITLE)}
            variant="primary"
            loading={isUndoingCancellation()}
            onClick={() => {
              void undoCancellation();
            }}
          />
        </div>
      }
    />
  );
};

export default UndoCancellationModal;
